import { Location } from '@angular/common';
import { Router } from '@angular/router';

export const SCORM_REDIRECT_URL_KEY = 'scormRedirectUrl';

/** Default fallback: My Courses listing. */
const DEFAULT_SCORM_RETURN_URL = '/modules';

export function setScormLaunchReturnUrl(url: string): void {
    const normalizedUrl = normalizeReturnUrl(url);
    sessionStorage.setItem(SCORM_REDIRECT_URL_KEY, normalizedUrl);
}

export function clearScormLaunchReturnUrl(): void {
    sessionStorage.removeItem(SCORM_REDIRECT_URL_KEY);
}

export function consumeScormLaunchReturnUrl(): string | null {
    const redirectUrl = sessionStorage.getItem(SCORM_REDIRECT_URL_KEY);
    if (!redirectUrl) {
        return null;
    }

    clearScormLaunchReturnUrl();
    return normalizeReturnUrl(redirectUrl);
}

/**
 * Navigate to a stored content-only return URL (catalog / my courses).
 * Handles iframe breakout when the top window is still on /launch.
 * Returns true when navigation was started.
 */
export function navigateToScormLaunchReturnUrl(): boolean {
    const targetUrl = consumeScormLaunchReturnUrl();
    if (!targetUrl) {
        return false;
    }

    const isInIframe = window.self !== window.top;
    if (isInIframe) {
        try {
            window.top!.location.href = targetUrl;
            return true;
        } catch {
            // Cannot navigate cross-origin parent – fall through to current window
        }
    }

    window.location.href = targetUrl;
    return true;
}

/** True when this frame is inside a SCORM launch shell that still shows /launch. */
export function isScormLaunchReturnFromIframe(): boolean {
    if (window.self === window.top) {
        return false;
    }

    try {
        return window.top!.location.pathname.includes('/launch');
    } catch {
        return false;
    }
}

/**
 * After SCORM closes it may redirect the iframe to module content while the top window
 * still shows the /launch URL. Break out to the top window or replace the launch segment.
 * When a content-only external return URL is stored, navigate there instead of module overview.
 * Returns true when ngOnInit should stop (navigation in progress).
 */
export function handleScormLaunchReturn(router: Router, location: Location): boolean {
    const isInIframe = window.self !== window.top;
    let isScormLaunchReturn = router.url.includes('/launch');

    if (isInIframe) {
        try {
            isScormLaunchReturn = window.top!.location.pathname.includes('/launch');
        } catch {
            // Cross-origin parent (e.g. MS Teams) – keep router-based detection
        }
    }

    if (!isScormLaunchReturn) {
        return false;
    }

    if (navigateToScormLaunchReturnUrl()) {
        return true;
    }

    if (isInIframe) {
        try {
            window.top!.location.href = `${window.location.pathname}${window.location.search}${window.location.hash}`;
            return true;
        } catch {
            // Cannot navigate cross-origin parent
        }
    }

    location.replaceState(router.url.replace('/launch', ''));

    return false;
}

function normalizeReturnUrl(url: string): string {
    if (!url) {
        return DEFAULT_SCORM_RETURN_URL;
    }

    return url.startsWith('/') ? url : `/${url}`;
}
